import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AgentStatusDot } from "../components/AgentStatusBadge.tsx";
import {
	api,
	ApiError,
	BRAND_TONE_STYLES,
	CONTENT_POLICY_COMPONENTS,
	CONTENT_POLICY_SECTIONS,
	type AgentInfo,
	type BusinessDTO,
	type BusinessProfile as BusinessProfileDTO,
	type ContentPolicy,
} from "../lib/api.ts";

export function BusinessProfile() {
	const { id } = useParams<{ id: string }>();
	const [profile, setProfile] = useState<BusinessProfileDTO | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!id) return;
		api
			.getBusinessProfile(id)
			.then(setProfile)
			.catch((err) => {
				if (err instanceof ApiError && err.status === 404) setError("Business not found.");
				else setError((err as Error).message);
			});
	}, [id]);

	if (error) {
		return (
			<div className="px-6 py-8">
				<Link to="/businesses" className="text-sm text-zinc-500 hover:text-zinc-300">
					← back to businesses
				</Link>
				<div className="mt-4 p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
					{error}
				</div>
			</div>
		);
	}

	if (!profile) {
		return <div className="px-6 py-8 text-zinc-500">Loading…</div>;
	}

	const b = profile.business;
	const tree = buildAgentTree(profile.agents);

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<Link to="/businesses" className="text-sm text-zinc-500 hover:text-zinc-300">
				← back to businesses
			</Link>

			<BusinessHeader
				business={b}
				onSaved={(updated) =>
					setProfile((p) => (p ? { ...p, business: { ...p.business, ...updated } } : p))
				}
			/>

			<ContentPolicyEditor
				business={b}
				onSaved={(updated) =>
					setProfile((p) => (p ? { ...p, business: { ...p.business, ...updated } } : p))
				}
			/>

			<section className="mt-8">
				<div className="flex items-baseline justify-between mb-3">
					<h2 className="text-lg font-semibold">Project-agents</h2>
					<Link to="/agents" className="text-xs text-zinc-500 hover:text-zinc-300">
						all agents →
					</Link>
				</div>

				{tree.length === 0 ? (
					<div className="text-sm text-zinc-500 italic">
						No dedicated project-agent yet. Managed directly by the manager-agent.
					</div>
				) : (
					<div className="border border-zinc-800 rounded-lg bg-zinc-900/30 divide-y divide-zinc-800">
						{tree.map((root) => (
							<AgentLink
								key={root.agent.key}
								node={root}
								depth={0}
							/>
						))}
					</div>
				)}
			</section>
		</div>
	);
}

interface AgentNodeData {
	agent: AgentInfo;
	children: AgentNodeData[];
}

function buildAgentTree(agents: AgentInfo[]): AgentNodeData[] {
	const byKey = new Map<string, AgentNodeData>();
	for (const a of agents) byKey.set(a.key, { agent: a, children: [] });
	const roots: AgentNodeData[] = [];
	for (const node of byKey.values()) {
		if (node.agent.parent && byKey.has(node.agent.parent)) {
			byKey.get(node.agent.parent)!.children.push(node);
		} else {
			roots.push(node);
		}
	}
	return roots;
}

function BusinessHeader({
	business,
	onSaved,
}: {
	business: BusinessDTO;
	onSaved: (updated: Partial<BusinessDTO>) => void;
}) {
	const [editing, setEditing] = useState(false);
	const [draftName, setDraftName] = useState(business.name);
	const [draftStage, setDraftStage] = useState(business.stage);
	const [draftMainRisk, setDraftMainRisk] = useState(business.main_risk ?? "");
	const [draftNotes, setDraftNotes] = useState(business.notes ?? "");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function startEdit() {
		setDraftName(business.name);
		setDraftStage(business.stage);
		setDraftMainRisk(business.main_risk ?? "");
		setDraftNotes(business.notes ?? "");
		setError(null);
		setEditing(true);
	}

	async function handleSave() {
		setBusy(true);
		setError(null);
		try {
			// Diff against current values so we don't waste a graph write on unchanged fields.
			const patch: {
				name?: string;
				stage?: string;
				main_risk?: string | null;
				notes?: string | null;
			} = {};
			if (draftName !== business.name) patch.name = draftName;
			if (draftStage !== business.stage) patch.stage = draftStage;
			const mainRisk = draftMainRisk.trim() === "" ? null : draftMainRisk;
			if (mainRisk !== (business.main_risk ?? null)) patch.main_risk = mainRisk;
			const notes = draftNotes.trim() === "" ? null : draftNotes;
			if (notes !== (business.notes ?? null)) patch.notes = notes;

			if (Object.keys(patch).length === 0) {
				setEditing(false);
				return;
			}

			const res = await api.updateBusiness(business.id, patch);
			onSaved(res.business);
			setEditing(false);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	if (editing) {
		return (
			<div className="mt-4 border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 space-y-3">
				<div>
					<label className="text-xs text-zinc-500">Name</label>
					<input
						type="text"
						value={draftName}
						onChange={(e) => setDraftName(e.target.value)}
						className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-100 text-base font-semibold focus:outline-none focus:border-zinc-500"
						disabled={busy}
					/>
					<div className="text-xs text-zinc-600 mt-1">
						{business.id} · tier {business.tier} · {business.mode} (immutable)
					</div>
				</div>
				<div>
					<label className="text-xs text-zinc-500">Stage</label>
					<textarea
						value={draftStage}
						onChange={(e) => setDraftStage(e.target.value)}
						rows={2}
						className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-200 text-sm resize-y focus:outline-none focus:border-zinc-500"
						disabled={busy}
					/>
				</div>
				<div>
					<label className="text-xs text-zinc-500">Main risk</label>
					<textarea
						value={draftMainRisk}
						onChange={(e) => setDraftMainRisk(e.target.value)}
						rows={2}
						placeholder="(leave empty to clear)"
						className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-200 text-sm resize-y placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500"
						disabled={busy}
					/>
				</div>
				<div>
					<label className="text-xs text-zinc-500">Notes</label>
					<textarea
						value={draftNotes}
						onChange={(e) => setDraftNotes(e.target.value)}
						rows={3}
						placeholder="(leave empty to clear)"
						className="mt-1 w-full px-3 py-1.5 bg-terminal border border-zinc-700 rounded text-zinc-300 text-sm resize-y placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500"
						disabled={busy}
					/>
				</div>
				{error && <div className="text-xs text-red-300">{error}</div>}
				<div className="flex gap-2 pt-1">
					<button
						type="button"
						onClick={handleSave}
						disabled={busy}
						className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
					>
						{busy ? "Saving…" : "Save"}
					</button>
					<button
						type="button"
						onClick={() => setEditing(false)}
						disabled={busy}
						className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
					>
						Cancel
					</button>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="mt-4 flex items-baseline justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold">{business.name}</h1>
					<div className="text-sm text-zinc-500 mt-1">
						{business.id} · tier {business.tier} · {business.mode}
					</div>
				</div>
				<button
					type="button"
					onClick={startEdit}
					className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
				>
					Edit
				</button>
			</div>

			<section className="mt-6 grid grid-cols-3 gap-4 text-sm">
				<KV label="Revenue/y" value={fmtMoney(business.revenue_year_k)} />
				<KV label="Dividends/y" value={fmtMoney(business.dividends_year_k)} />
				<KV
					label="Margin"
					value={business.margin_pct !== null ? `${business.margin_pct}%` : "—"}
				/>
			</section>

			<section className="mt-4 space-y-2 text-sm">
				<div className="text-zinc-500">Stage</div>
				<div className="text-zinc-200">{business.stage}</div>
				{business.main_risk && (
					<>
						<div className="text-zinc-500 mt-2">Main risk</div>
						<div className="text-amber-300/90">{business.main_risk}</div>
					</>
				)}
				{business.latest_status_summary && (
					<>
						<div className="text-zinc-500 mt-2">Current status</div>
						<div className="text-zinc-200">{business.latest_status_summary}</div>
					</>
				)}
				{business.notes && (
					<>
						<div className="text-zinc-500 mt-2">Notes</div>
						<div className="text-zinc-400 italic">{business.notes}</div>
					</>
				)}
			</section>
		</>
	);
}

function ContentPolicyEditor({
	business,
	onSaved,
}: {
	business: BusinessDTO;
	onSaved: (updated: Partial<BusinessDTO>) => void;
}) {
	const initial = parsePolicy(business.contentPolicy);
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState<ContentPolicy>(initial);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function startEdit() {
		setDraft(parsePolicy(business.contentPolicy));
		setError(null);
		setEditing(true);
	}

	async function handleSave() {
		// Guardrail: at least one section enabled — otherwise head-of-design would produce an empty brief.
		const sectionsEnabled = CONTENT_POLICY_SECTIONS.filter(
			(s) => draft.sections?.[s] !== false,
		).length;
		if (sectionsEnabled === 0) {
			setError("At least one section must be enabled.");
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const policy: ContentPolicy = { ...draft, schemaVersion: 1 };
			const json = JSON.stringify(policy);
			const res = await api.updateBusiness(business.id, { contentPolicy: json });
			onSaved(res.business);
			setEditing(false);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	const enabledSections = CONTENT_POLICY_SECTIONS.filter((s) => initial.sections?.[s] !== false);
	const enabledComponents = CONTENT_POLICY_COMPONENTS.filter(
		(c) => initial.components?.[c] !== false,
	);

	return (
		<section className="mt-8 border border-zinc-800 rounded-lg bg-zinc-900/30 p-4">
			<div className="flex items-baseline justify-between mb-3">
				<div>
					<h2 className="text-lg font-semibold">Content Policy</h2>
					<div className="text-xs text-zinc-500 mt-0.5">
						Filters head-of-design-review's brief: sections, components, locale, tone.
						{business.contentPolicyUpdatedAt && (
							<> Last updated {business.contentPolicyUpdatedAt.slice(0, 19).replace("T", " ")}.</>
						)}
					</div>
				</div>
				{!editing && (
					<button
						type="button"
						onClick={startEdit}
						className="text-xs px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
					>
						Edit
					</button>
				)}
			</div>

			{!editing ? (
				<div className="text-sm text-zinc-400 grid grid-cols-2 gap-3">
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">Sections enabled ({enabledSections.length})</div>
						<div className="font-mono text-xs mt-0.5">{enabledSections.join(", ") || "(none)"}</div>
					</div>
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">Components enabled ({enabledComponents.length})</div>
						<div className="font-mono text-xs mt-0.5">{enabledComponents.join(", ") || "(none)"}</div>
					</div>
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">i18n</div>
						<div className="text-xs mt-0.5">
							{initial.i18n?.locales?.join(", ") ?? "(default)"} {initial.i18n?.default && (
								<span className="text-zinc-500">· default: {initial.i18n.default}</span>
							)}
						</div>
					</div>
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">Brand tone</div>
						<div className="text-xs mt-0.5">
							{initial.brandTone?.style ?? "(default)"}
							{initial.brandTone?.premium && " · premium"}
							{initial.brandTone?.playful && " · playful"}
							{initial.brandTone?.minimal && " · minimal"}
						</div>
					</div>
					{initial.notes && (
						<div className="col-span-2">
							<div className="text-xs text-zinc-500 uppercase tracking-wide">Notes</div>
							<div className="text-xs mt-0.5 italic whitespace-pre-wrap">{initial.notes}</div>
						</div>
					)}
				</div>
			) : (
				<div className="space-y-4">
					{/* Sections */}
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Sections (page routes)</div>
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-sm">
							{CONTENT_POLICY_SECTIONS.map((s) => (
								<label key={s} className="flex items-center gap-2">
									<input
										type="checkbox"
										checked={draft.sections?.[s] !== false}
										onChange={(e) =>
											setDraft({
												...draft,
												sections: { ...(draft.sections ?? {}), [s]: e.target.checked },
											})
										}
									/>
									<span className="font-mono text-xs">{s}</span>
								</label>
							))}
						</div>
					</div>

					{/* Components */}
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Shared components</div>
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-sm">
							{CONTENT_POLICY_COMPONENTS.map((c) => (
								<label key={c} className="flex items-center gap-2">
									<input
										type="checkbox"
										checked={draft.components?.[c] !== false}
										onChange={(e) =>
											setDraft({
												...draft,
												components: { ...(draft.components ?? {}), [c]: e.target.checked },
											})
										}
									/>
									<span className="font-mono text-xs">{c}</span>
								</label>
							))}
						</div>
					</div>

					{/* i18n + brandTone + paletteHints */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Locales (comma-separated)</div>
							<input
								type="text"
								className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm font-mono"
								placeholder="de, en"
								value={(draft.i18n?.locales ?? []).join(", ")}
								onChange={(e) => {
									const locales = e.target.value
										.split(",")
										.map((x) => x.trim())
										.filter(Boolean);
									setDraft({ ...draft, i18n: { ...(draft.i18n ?? {}), locales } });
								}}
							/>
							<div className="mt-1">
								<span className="text-xs text-zinc-500 mr-2">default:</span>
								<input
									type="text"
									className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs font-mono"
									placeholder="de"
									value={draft.i18n?.default ?? ""}
									onChange={(e) =>
										setDraft({
											...draft,
											i18n: { ...(draft.i18n ?? {}), default: e.target.value.trim() || undefined },
										})
									}
								/>
							</div>
						</div>

						<div>
							<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Brand tone style</div>
							<select
								className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm"
								value={draft.brandTone?.style ?? ""}
								onChange={(e) =>
									setDraft({
										...draft,
										brandTone: { ...(draft.brandTone ?? {}), style: e.target.value || undefined },
									})
								}
							>
								<option value="">(unset)</option>
								{BRAND_TONE_STYLES.map((s) => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</select>
							<div className="mt-1 flex flex-wrap gap-3 text-xs">
								{(["playful", "premium", "minimal"] as const).map((flag) => (
									<label key={flag} className="flex items-center gap-1">
										<input
											type="checkbox"
											checked={!!draft.brandTone?.[flag]}
											onChange={(e) =>
												setDraft({
													...draft,
													brandTone: { ...(draft.brandTone ?? {}), [flag]: e.target.checked },
												})
											}
										/>
										{flag}
									</label>
								))}
							</div>
						</div>
					</div>

					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Palette hints</div>
						<div className="flex flex-wrap items-center gap-3 text-sm">
							<label className="flex items-center gap-2 text-xs">
								<input
									type="checkbox"
									checked={!!draft.paletteHints?.darkSurface}
									onChange={(e) =>
										setDraft({
											...draft,
											paletteHints: { ...(draft.paletteHints ?? {}), darkSurface: e.target.checked },
										})
									}
								/>
								darkSurface
							</label>
							<input
								type="text"
								placeholder="accent (e.g. amber-gold)"
								className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs font-mono w-44"
								value={draft.paletteHints?.accent ?? ""}
								onChange={(e) =>
									setDraft({
										...draft,
										paletteHints: { ...(draft.paletteHints ?? {}), accent: e.target.value || undefined },
									})
								}
							/>
							<input
								type="text"
								placeholder="secondary (e.g. indigo)"
								className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs font-mono w-44"
								value={draft.paletteHints?.secondary ?? ""}
								onChange={(e) =>
									setDraft({
										...draft,
										paletteHints: {
											...(draft.paletteHints ?? {}),
											secondary: e.target.value || undefined,
										},
									})
								}
							/>
						</div>
					</div>

					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Notes (free-form, head-of-design-review reads these)</div>
						<textarea
							rows={5}
							className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm"
							value={draft.notes ?? ""}
							onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
						/>
					</div>

					{error && (
						<div className="p-2 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
							{error}
						</div>
					)}

					<div className="flex gap-2">
						<button
							type="button"
							onClick={handleSave}
							disabled={busy}
							className="px-3 py-1.5 text-sm rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50"
						>
							{busy ? "Saving…" : "Save policy"}
						</button>
						<button
							type="button"
							onClick={() => setEditing(false)}
							disabled={busy}
							className="px-3 py-1.5 text-sm rounded border border-zinc-700 hover:border-zinc-500"
						>
							Cancel
						</button>
					</div>
				</div>
			)}
		</section>
	);
}

function parsePolicy(raw: string | null): ContentPolicy {
	if (!raw) return {};
	try {
		return JSON.parse(raw) as ContentPolicy;
	} catch {
		return {};
	}
}

function AgentLink({ node, depth }: { node: AgentNodeData; depth: number }) {
	return (
		<>
			<Link
				to={`/agents/${node.agent.key}`}
				className="px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/40 transition-colors"
				style={{ paddingLeft: 16 + depth * 24 }}
			>
				{depth > 0 && <span className="text-zinc-700 text-xs">└─</span>}
				<AgentStatusDot label={node.agent.key} />
				<div className="min-w-0 flex-1">
					<div className="text-sm font-medium text-zinc-100">{node.agent.displayName}</div>
					<div className="text-xs text-zinc-500 font-mono">{node.agent.key}</div>
				</div>
				<span className="text-zinc-600 text-sm">→</span>
			</Link>
			{node.children.map((child) => (
				<AgentLink key={child.agent.key} node={child} depth={depth + 1} />
			))}
		</>
	);
}

function KV({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<div className="text-xs text-zinc-500">{label}</div>
			<div className="text-zinc-100 font-medium">{value}</div>
		</div>
	);
}

function fmtMoney(k: number | null): string {
	if (k === null || k === 0) return "—";
	if (k >= 1000) return `${(k / 1000).toFixed(1)}M`;
	return `${k}K`;
}
